import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle, GroupType, IssueBadge } from '@wix/rules-panel-ui';
import React, { useCallback, useRef } from 'react';
import { GroupLoadingSkeleton } from '../../components/wizard-content/group-loading-skeleton';
import { VerifyPageButton } from '../../components/wizard-content/verify-page-button';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useQueriesExecutionResultsByItemType } from '../../hooks/use-queries-execution-results-by-item-type';
import { getBreakdownPerTaskVerticalPage } from '../../utils/get-breakdown-per-task-vertical-page';
import { getScanFailureContent } from '../../utils/get-scan-failure-message';
import { resolveGroupAnalysisState } from '../../utils/resolve-group-analysis-state';
import { getNumberOfIssuesInVerticalGroup } from '../shared/helpers';
import { useVerify } from '../shared/use-verify';
import { getSubtitles } from './get-subtitles';
export const useBuildVerticalRuleGroups = () => {
  const hasSentBiEventRef = useRef({});
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const addErrorModals = experiments.enabled('specs.a11y.addErrorModals');
  const {
    getPagesData,
    panelState,
    getGroupData
  } = usePanelStateAtom();
  const tpaPageIds = Array.from(getPagesData().entries()).filter(_ref => {
    let [_, {
      tpaPage
    }] = _ref;
    return tpaPage;
  }).map(_ref2 => {
    let [pageId, _] = _ref2;
    return pageId;
  });
  useQueriesExecutionResultsByItemType(tpaPageIds);
  const pageIdToPageData = getPagesData();
  const {
    verify
  } = useVerify({});
  const logPageScanFinished = useCallback((pageId, groupData, issuesCount) => {
    var _groupData$analysisDa;
    biLogger.pageScanFinished({
      analysisJobId: (groupData == null || (_groupData$analysisDa = groupData.analysisData) == null ? void 0 : _groupData$analysisDa.analysisJobId) ?? '',
      issuesCount,
      pageId,
      pageType: groupData.itemType
    });
    hasSentBiEventRef.current[pageId] = true;
  }, [biLogger]);
  const logBreakdownPerTaskVerticalPages = useCallback(groupData => {
    const ruleElements = getBreakdownPerTaskVerticalPage(groupData);
    ruleElements.forEach(ruleElement => {
      biLogger.scanSiteFinishedBreakdownPerTaskVerticalsPages(ruleElement);
    });
  }, [biLogger]);
  const shouldSendBiEvent = useCallback(pageId => {
    return hasSentBiEventRef.current[pageId] !== true;
  }, []);
  const buildVerticalLevelRuleGroup = useCallback(props => {
    var _pageIdToPageData$get;
    const {
      pageId,
      onClick
    } = props;
    const groupData = getGroupData(pageId);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${props.pageId}.`);
    }
    const itemType = groupData.itemType;
    const totalPagesToScan = ((_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null || (_pageIdToPageData$get = _pageIdToPageData$get.innerRoutes) == null ? void 0 : _pageIdToPageData$get.length) ?? 0;
    const scannedPagesAmount = groupData.scannedPages ?? 0;
    const {
      title,
      analysisState,
      failureReason
    } = groupData;
    const handleVerifyClick = async () => {
      var _panelState$globalSec;
      await verify(groupData, [...Array.from(((_panelState$globalSec = panelState.globalSectionComponentsDataMap) == null ? void 0 : _panelState$globalSec.values()) ?? []).map(_ref3 => {
        let {
          componentId
        } = _ref3;
        return componentId;
      })]);
    };
    const {
      isFirstAnalysis,
      isAnalyzeFailed,
      isAnalyzing: isAnalyzingState,
      isFetchResultSucceeded,
      isFetchResultsFailed
    } = resolveGroupAnalysisState(analysisState);
    const numberOfIssues = isFetchResultSucceeded ? getNumberOfIssuesInVerticalGroup(panelState, itemType) : 0;
    const isAnalyzing = isAnalyzingState || isFirstAnalysis;
    if (isFetchResultSucceeded && shouldSendBiEvent(pageId)) {
      logPageScanFinished(pageId, groupData, numberOfIssues);
      logBreakdownPerTaskVerticalPages(groupData);
    } else if ((isFetchResultsFailed || isAnalyzeFailed) && shouldSendBiEvent(pageId)) {
      logPageScanFinished(pageId, groupData, -1);
      logBreakdownPerTaskVerticalPages(groupData);
    }
    return {
      disabled: !isFetchResultSucceeded,
      expansionMode: GroupExpansionStyle.button,
      groupId: pageId,
      groupType: GroupType.StaticPage,
      onGroupToggle: onClick,
      ruleGroupContent: {
        badge: isAnalyzeFailed ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed')
        }) : numberOfIssues !== 0 && isFetchResultSucceeded ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label', {
            numberOfIssues
          })
        }) : undefined,
        badgeTooltipContent: isAnalyzeFailed ? addErrorModals ? getScanFailureContent(t, failureReason).tooltip : t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
        collapsedNode: !isFetchResultSucceeded && !isAnalyzeFailed ? /*#__PURE__*/React.createElement(GroupLoadingSkeleton, null) : isAnalyzeFailed || isAnalyzing ? /*#__PURE__*/React.createElement(Box, {
          padding: "0 3px"
        }, /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: pageId,
          taskCount: 0,
          onClick: handleVerifyClick
        })) : undefined,
        groupLabel: title,
        subtitles: !isAnalyzeFailed ? getSubtitles({
          groupData,
          scannedPagesAmount,
          t,
          totalPagesToScan
        }) : undefined
      },
      ruleResults: []
    };
  }, [getGroupData, pageIdToPageData, panelState, t, verify, logPageScanFinished, logBreakdownPerTaskVerticalPages, shouldSendBiEvent, addErrorModals]);
  return {
    buildVerticalLevelRuleGroup
  };
};
//# sourceMappingURL=use-build-vertical-group.js.map